import React, { useEffect, useRef, useState } from "react";
import { Button, Alert, Modal } from "@repo/ui";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next"; // ✅ Import i18n
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useAppDispatch";
import { setEvent } from "../../../../../redux/slices/eventBusSlice";
import {
  setCompletedWithdrawTotal,
  setPendingReviewWithdrawTotal,
  setHasPendingReview,
} from "../../../../../redux/slices/withdrawalsSlice";
import { RootState } from "../../../../../redux/store";
import { generateGetURL } from "../../../../../services/mediaApiService";
import {
  getSuccessfulWithdrawalsCountByGiftTable,
  listWithdrawals,
} from "../../../../../services/withdrawApiService";
import { sumGiftsByGiftTableID } from "../../../../../services/giftApiService";
import placeHolderImage from "../../../../../assets/placeholders/picture.jpg";
import gsap from "gsap";
import { withTranslations } from "../../../../../hoc/withTranslations/withTranslations";
import { setStickyOffset } from "../../../../../redux/slices/uiSlice"; // Import the action

const LOCALE =
  "modules_MyGiftTable_components_WithdrawSection_web_WebWithdrawSection";
const WebWithdrawSection: React.FC = () => {
  const { t, i18n } = useTranslation(LOCALE); // ✅ Correct translation namespace

  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info";
  } | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const [completedWithdrawTotal, setCompletedWithdrawTotalState] = useState(0);
  const [pendingReviewWithdrawTotal, setPendingReviewWithdrawTotalState] =
    useState(0);
  const [hasPendingReview, setHasPendingReviewState] = useState(false);
  const [collectedSum, setCollectedSum] = useState(0);
  const [withdrawalCount, setWithdrawalCount] = useState(0); // New state

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const totalValue = useAppSelector((state) => state.giftTable.totalValue);

  const tableID = useAppSelector((state: RootState) => state.giftTable.ID);
  const status = useAppSelector((state: RootState) => state.giftTable.status);
  const { profilePicture, products } = useAppSelector(
    (state: RootState) => state.giftTable
  );
  const userID = useAppSelector((state: RootState) => state.auth.user?.id);

  useEffect(() => {
    const fetchWithdrawalCount = async () => {
      if (tableID) {
        try {
          const countResponse =
            await getSuccessfulWithdrawalsCountByGiftTable(tableID);
          setWithdrawalCount(countResponse.count);
        } catch (error) {
          console.error("Error fetching withdrawal count", error);
          setWithdrawalCount(0);
        }
      }
    };

    fetchWithdrawalCount();
  }, [tableID]);
  // Update the sticky offset based on the table status.
  // When status is "draft", set the offset to 60 (adjust if necessary).
  useEffect(() => {
    if (status === "draft") {
      dispatch(setStickyOffset(60));
    } else {
      dispatch(setStickyOffset(0));
    }
  }, [status, dispatch]);

  const physicalProductsCollectedValue =
    products
      ?.filter((product) => product.isPhysical && product.taken > 0)
      .reduce((sum, product) => sum + product.value * product.taken, 0) || 0;

  const collectedMoney = collectedSum - physicalProductsCollectedValue;

  useEffect(() => {
    const fetchProfileImage = async () => {
      if (profilePicture) {
        try {
          const media = await generateGetURL(profilePicture);
          setProfileImageUrl(media.signedURL);
        } catch (error) {
          console.error(t("errorFetchingProfile"), error);
          setProfileImageUrl(null);
        }
      }
    };
    fetchProfileImage();
  }, [profilePicture]);

  useEffect(() => {
    if (tableID) {
      sumGiftsByGiftTableID(tableID)
        .then((result) => setCollectedSum(result.sum))
        .catch((error) => {
          console.error(t("errorFetchingCollectedSum"), error);
          setCollectedSum(0);
        });
    }
  }, [tableID]);

  useEffect(() => {
    const fetchWithdrawals = async () => {
      try {
        const withdrawals = await listWithdrawals(
          userID as string,
          undefined,
          tableID
        );
        const completedSum = withdrawals
          .filter((w) => w.status === "payment_completed")
          .reduce((sum, w) => sum + w.amount, 0);
        const pendingReviewSum = withdrawals
          .filter((w) => w.status === "pending_review")
          .reduce((sum, w) => sum + w.amount, 0);
        const hasPending = withdrawals.some(
          (w) => w.status === "pending_review"
        );

        setCompletedWithdrawTotalState(completedSum);
        setPendingReviewWithdrawTotalState(pendingReviewSum);
        setHasPendingReviewState(hasPending);

        dispatch(setCompletedWithdrawTotal(completedSum));
        dispatch(setPendingReviewWithdrawTotal(pendingReviewSum));
        dispatch(setHasPendingReview(hasPending));
      } catch (error) {
        console.error(t("errorFetchingWithdrawals"), error);
      }
    };

    fetchWithdrawals();
  }, []);

  const formatCurrency = (value: number) =>
    value.toLocaleString(i18n.language, { style: "currency", currency: "MXN" });

  const getWithdrawButtonState = () => {
    if (hasPendingReview) {
      return {
        disabled: true,
        altText: `Tienes un retiro en curso de ${
          pendingReviewWithdrawTotal?.toLocaleString("es-MX", {
            style: "currency",
            currency: "MXN",
          }) || "0.00"
        }. Podrás iniciar el proceso nuevamente al completar el retiro actual.`,
      };
    } else if (status === "draft") {
      return {
        disabled: true,
        altText:
          "Tu mesa de regalos aún no está activa. Publícala ahora para poder realizar un retiro",
      };
    } else if (totalValue === 0) {
      return {
        disabled: true,
        altText: "El valor de tu mesa es $0.00",
      };
    } else if (collectedSum === 0) {
      return {
        disabled: true,
        altText:
          "Tu total recaudado es $0.00, comparte tu mesa con tus familiares y amigos para conseguir tus primeros regalos!",
      };
    } else {
      return {
        disabled: false,
        altText: "",
      };
    }
  };

  const { disabled, altText } = getWithdrawButtonState();

  const handleWithdrawClick = () => {
    if (withdrawalCount === 0) {
      setIsModalOpen(true);
    } else {
      navigate("/my-table/withdraw/step1");
    }
  };

  const shouldHighlightShareButton = (message: string) => {
    return (
      message.includes("Tu mesa de regalos aún no está activa") ||
      message.includes("Tu total recaudado es $0.00")
    );
  };

  const showAlert = (message: React.ReactNode) => {
    setAlert({ message, level: "info" });
    if (typeof message === "string" && shouldHighlightShareButton(message)) {
      dispatch(
        setEvent({
          event: "myGiftTable-withDrawSection-highlightShareButton",
          value: true,
        })
      );
    } else {
      dispatch(
        setEvent({
          event: "myGiftTable-withDrawSection-highlightShareButton",
          value: false,
        })
      );
    }
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
    setTimeout(() => {
      setAlert(null);
      dispatch(
        setEvent({
          event: "myGiftTable-withDrawSection-highlightShareButton",
          value: false,
        })
      );
    }, 4000);
  };

  const handleButtonHover = () => {
    if (disabled && altText) {
      showAlert(altText);
    }
  };

  return (
    <>
      {alert && (
        <div ref={alertRef} className="fixed top-4 left-0 right-0 z-50">
          <Alert message={alert.message} level={alert.level} />
        </div>
      )}
      {status === "draft" ? (
        <div className="sticky top-0 z-10 w-full p-4 bg-yellow-100 text-yellow-700 text-sm flex items-center justify-between">
          <span>{t("tableNotActive")}</span>
          <Button
            onClick={() => navigate("/my-table/choose-plan")}
            type="primary"
            className="ml-4"
          >
            {t("publishNow")}
          </Button>
        </div>
      ) : (
        <div className="flex justify-end items-center p-4 bg-white space-x-12">
          <div className="text-right">
            <p className="text-sm text-gray-600">{t("collected")}</p>
            <p className="text-lg font-bold text-gray-900">
              {formatCurrency(collectedMoney)}
            </p>
          </div>

          {physicalProductsCollectedValue > 0 && (
            <div className="text-right">
              <p className="text-sm text-secondary">{t("physicalProducts")}</p>
              <p className="text-lg font-bold text-secondary">
                {formatCurrency(physicalProductsCollectedValue)}
              </p>
            </div>
          )}

          {completedWithdrawTotal > 0 && (
            <div className="text-right">
              <p className="text-sm text-gray-600">{t("withdrawn")}</p>
              <p className="text-lg font-bold text-green-600">
                {formatCurrency(completedWithdrawTotal)}
              </p>
            </div>
          )}

          {pendingReviewWithdrawTotal > 0 && (
            <div className="text-right">
              <p className="text-sm text-yellow-600">
                {t("withdrawInProgress")}
              </p>
              <p className="text-lg font-bold text-yellow-600">
                {formatCurrency(pendingReviewWithdrawTotal)}
              </p>
            </div>
          )}

          <Button
            children={t("withdrawButton")}
            onMouseEnter={handleButtonHover}
            onClick={disabled ? handleButtonHover : handleWithdrawClick}
            type="primary"
            disabled={disabled}
            className={`${
              disabled
                ? "hover:bg-gray-300 hover:text-gray-700 cursor-not-allowed"
                : ""
            }`}
          />
        </div>
      )}

      {/* Modal for first-time withdraw */}
      <Modal className="z-50" isOpen={isModalOpen}>
        <div className="text-center">
          <h2 className="text-lg font-bold mb-4">{t("howToWithdraw")}</h2>
          <img
            src={profileImageUrl || placeHolderImage}
            alt="Event"
            className="w-96 h-96 rounded-full object-cover mx-auto mb-4"
          />
          <p className="text-gray-700 mb-6">{t("withdrawInstructions")}</p>
          <Button
            children={t("startWithdraw")}
            onClick={() => navigate("/my-table/withdraw/step1")}
            type="primary"
            className="w-full mb-4"
          />
          <Button
            children={t("withdrawLater")}
            onClick={() => setIsModalOpen(false)}
            type="secondary"
            className="w-full"
          />
        </div>
      </Modal>
    </>
  );
};

export default withTranslations(LOCALE)(WebWithdrawSection);
